import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiDollarSign, FiClock, FiUsers, FiArrowRight, FiStar, FiDownload } = FiIcons;

const CaseStudies = () => {
  const navigate = useNavigate();

  const handleStartProject = () => {
    navigate('/contact');
  };

  const handleScheduleConsultation = () => {
    navigate('/contact');
  };

  const handleViewPortfolio = () => {
    navigate('/case-studies');
  };

  const handleDownloadCaseStudies = () => {
    // Create a comprehensive case study document
    const caseStudyData = {
      title: "aiximo.com Case Studies - Detailed Report",
      date: new Date().toLocaleDateString(),
      studies: caseStudies,
      summary: {
        totalProjects: "50+",
        averageCostReduction: "60%",
        averageTimeReduction: "65%",
        clientSatisfaction: "95%"
      }
    };

    const content = `
# aiximo.com Case Studies - Detailed Report

Generated on: ${caseStudyData.date}

## Executive Summary
- Total Projects Completed: ${caseStudyData.summary.totalProjects}
- Average Cost Reduction: ${caseStudyData.summary.averageCostReduction}
- Average Development Time Reduction: ${caseStudyData.summary.averageTimeReduction}
- Client Satisfaction Rate: ${caseStudyData.summary.clientSatisfaction}

${caseStudyData.studies.map(study => `
## ${study.title}

**Industry:** ${study.industry}
**Client:** ${study.client}

### Challenge
${study.challenge}

### Our AI-Accelerated Solution
${study.solution}

### Results Achieved
${study.results.map(result => `- ${result.metric}: ${result.value}`).join('\n')}

### Client Testimonial
"${study.testimonial}"
— ${study.author}

---
`).join('')}

## About aiximo.com

We are your specialized AI-powered development agency, delivering applications faster, smarter, and more affordably. Our unique blend of human expertise and AI-driven efficiency ensures exceptional results for startups, SMBs, and enterprises.

Contact us: info@aiximo.com
Website: aiximo.com

© 2024 aiximo.com. All rights reserved.
    `;

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aiximo-case-studies-${new Date().toISOString().split('T')[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const caseStudies = [
    {
      title: "FinTech Mobile Banking Revolution",
      industry: "FinTech",
      client: "NextGen Banking",
      challenge: "Slow time-to-market for a new mobile banking app with complex security requirements and real-time transaction processing.",
      solution: "Leveraged AI for automated code generation and intelligent testing frameworks, implementing advanced security protocols with AI-assisted vulnerability detection.",
      results: [
        { metric: "Development time reduced", value: "65%" },
        { metric: "Cost savings achieved", value: "40%" },
        { metric: "Security vulnerabilities prevented", value: "100%" },
        { metric: "Launch timeline", value: "3 months ahead" }
      ],
      testimonial: "aiximo.com transformed our development process. What would have taken 18 months was delivered in 6 months with superior quality and security.",
      author: "Sarah Chen, CTO at NextGen Banking",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop"
    },
    {
      title: "E-commerce Platform Optimization",
      industry: "E-commerce",
      client: "ShopSmart Solutions",
      challenge: "High development costs for a complex data analytics platform with real-time inventory management and personalized recommendations.",
      solution: "Utilized prompt engineering to rapidly develop core functionalities and AI-powered analytics engine for personalized user experiences.",
      results: [
        { metric: "Development costs reduced", value: "55%" },
        { metric: "Performance improvement", value: "80%" },
        { metric: "User engagement increased", value: "120%" },
        { metric: "Revenue growth", value: "45%" }
      ],
      testimonial: "The AI-driven approach exceeded our expectations. Our platform now processes millions of transactions seamlessly with intelligent recommendations.",
      author: "Michael Rodriguez, Founder of ShopSmart",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop"
    },
    {
      title: "Healthcare Data Management System",
      industry: "Healthcare",
      client: "MedFlow Technologies",
      challenge: "Complex regulatory compliance requirements and need for secure, scalable patient data management with AI-powered diagnostics support.",
      solution: "Implemented AI-enhanced security protocols and automated compliance checking, with intelligent data processing for diagnostic assistance.",
      results: [
        { metric: "Compliance accuracy", value: "99.9%" },
        { metric: "Data processing speed", value: "300% faster" },
        { metric: "Development timeline", value: "50% reduced" },
        { metric: "Security incidents", value: "Zero" }
      ],
      testimonial: "aiximo.com delivered a robust, compliant solution that revolutionized our patient care capabilities while ensuring absolute data security.",
      author: "Dr. Jennifer Walsh, Chief Medical Officer",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop"
    },
    {
      title: "EdTech Learning Platform",
      industry: "Education Technology",
      client: "LearnSphere Inc.",
      challenge: "Creating an adaptive learning platform with personalized content delivery and real-time performance analytics for students and educators.",
      solution: "Developed AI-powered content recommendation engine and automated assessment tools with intelligent progress tracking.",
      results: [
        { metric: "Student engagement", value: "90% increase" },
        { metric: "Learning outcomes", value: "75% improvement" },
        { metric: "Platform scalability", value: "10x capacity" },
        { metric: "Teacher productivity", value: "60% increase" }
      ],
      testimonial: "The AI-driven personalization has transformed how our students learn. Engagement and outcomes have never been better.",
      author: "Prof. David Kim, Educational Director",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
    }
  ];

  const stats = [
    { number: "50+", label: "Projects Completed" },
    { number: "95%", label: "Client Satisfaction" },
    { number: "60%", label: "Average Cost Reduction" },
    { number: "3x", label: "Faster Development" }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Success Stories
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Real Results, AI-Accelerated. Explore how aiximo.com has helped businesses like yours overcome development challenges, achieve rapid growth, and launch innovative products with our AI-accelerated solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {caseStudies.map((study, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-2 ${index % 2 === 1 ? 'lg:grid-cols-2' : ''}`}>
                  <div className={`p-8 lg:p-12 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="flex items-center mb-4">
                      <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                        {study.industry}
                      </span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                      {study.title}
                    </h3>
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Challenge:</h4>
                      <p className="text-gray-600">{study.challenge}</p>
                    </div>
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Our AI-Accelerated Solution:</h4>
                      <p className="text-gray-600">{study.solution}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600 mb-1">{result.value}</div>
                          <div className="text-sm text-gray-600">{result.metric}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <div className="flex items-start">
                        <SafeIcon icon={FiStar} className="h-5 w-5 text-yellow-500 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-gray-700 italic mb-3">"{study.testimonial}"</p>
                          <p className="text-sm font-medium text-gray-900">— {study.author}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-64 lg:h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Transform Your Development Process?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Choose from our comprehensive AI-accelerated services or let us create a custom solution for your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleScheduleConsultation}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Schedule a Consultation
              </button>
              <button
                onClick={handleStartProject}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-600 hover:text-white transition-all duration-300"
              >
                Start Your Project
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Join our growing list of successful clients who have transformed their businesses with AI-accelerated development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleStartProject}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
              >
                Start Your Project
                <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
              </button>
              <button
                onClick={handleDownloadCaseStudies}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 flex items-center justify-center"
              >
                <SafeIcon icon={FiDownload} className="mr-2 h-5 w-5" />
                Download Case Study Details
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;